import { FC, useState } from 'react';
import {
    Box,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Chip,
    Stack,
    Paper,
    Collapse,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

export interface FilterOption {
    label: string;
    value: string | number | boolean;
}

export interface FilterConfig {
    field: string;
    label: string;
    options?: FilterOption[];
    type?: 'select' | 'multiselect' | 'date' | 'daterange';
}

export interface DataTableFiltersProps {
    searchValue: string;
    onSearchChange: (value: string) => void;
    filters?: FilterConfig[];
    activeFilters?: Record<string, any>;
    onFilterChange?: (field: string, value: any) => void;
    onClearFilters?: () => void;
    placeholder?: string;
}

export const DataTableFilters: FC<DataTableFiltersProps> = ({
    searchValue,
    onSearchChange,
    filters = [],
    activeFilters = {},
    onFilterChange,
    onClearFilters,
    placeholder = 'Search...',
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [filtersExpanded, setFiltersExpanded] = useState(!isMobile);

    const activeFilterCount = Object.keys(activeFilters).filter(
        (key) => {
            const value = activeFilters[key];
            if (value === '' || value === undefined || value === null) return false;
            // Check for date range
            if (typeof value === 'object' && 'from' in value && 'to' in value) {
                return value.from || value.to;
            }
            return true;
        }
    ).length;

    const hasActiveFilters = activeFilterCount > 0 || searchValue !== '';

    const renderFilter = (filter: FilterConfig) => {
        if (filter.type === 'daterange') {
            const dateRange = activeFilters[filter.field] || { from: null, to: null };

            return (
                <LocalizationProvider key={filter.field} dateAdapter={AdapterDateFns}>
                    <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <DatePicker
                            label={`${filter.label} From`}
                            value={dateRange.from ? new Date(dateRange.from) : null}
                            onChange={(newValue) => {
                                onFilterChange?.(filter.field, {
                                    ...dateRange,
                                    from: newValue ? newValue.toISOString() : null,
                                });
                            }}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    sx: { minWidth: { xs: '100%', sm: 160 } },
                                },
                            }}
                        />
                        <DatePicker
                            label={`${filter.label} To`}
                            value={dateRange.to ? new Date(dateRange.to) : null}
                            onChange={(newValue) => {
                                onFilterChange?.(filter.field, {
                                    ...dateRange,
                                    to: newValue ? newValue.toISOString() : null,
                                });
                            }}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    sx: { minWidth: { xs: '100%', sm: 160 } },
                                },
                            }}
                            minDate={dateRange.from ? new Date(dateRange.from) : undefined}
                        />
                    </Box>
                </LocalizationProvider>
            );
        }

        return (
            <FormControl
                key={filter.field}
                size="small"
                sx={{ minWidth: { xs: '100%', sm: 200 } }}
            >
                <InputLabel>{filter.label}</InputLabel>
                <Select
                    value={activeFilters[filter.field] || ''}
                    onChange={(e) => onFilterChange?.(filter.field, e.target.value)}
                    label={filter.label}
                    startAdornment={
                        <InputAdornment position="start">
                            <FilterListIcon fontSize="small" color="action" />
                        </InputAdornment>
                    }
                >
                    <MenuItem value="">
                        <em>All</em>
                    </MenuItem>
                    {filter.options?.map((option) => (
                        <MenuItem key={String(option.value)} value={String(option.value)}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    };

    const getFilterDisplayValue = (filter: FilterConfig, value: any): string => {
        if (filter.type === 'daterange' && typeof value === 'object') {
            const fromDate = value.from ? new Date(value.from).toLocaleDateString() : '';
            const toDate = value.to ? new Date(value.to).toLocaleDateString() : '';
            if (fromDate && toDate) return `${fromDate} - ${toDate}`;
            if (fromDate) return `From ${fromDate}`;
            if (toDate) return `To ${toDate}`;
            return '';
        }

        const option = filter.options?.find((opt) => opt.value === value);
        return option?.label || String(value);
    };

    return (
        <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
            <Stack spacing={2}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexDirection: { xs: 'column', md: 'row' } }}>
                    <TextField
                        fullWidth
                        value={searchValue}
                        onChange={(e) => onSearchChange(e.target.value)}
                        placeholder={placeholder}
                        variant="outlined"
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: searchValue && (
                                <InputAdornment position="end">
                                    <Button
                                        size="small"
                                        onClick={() => onSearchChange('')}
                                        sx={{ minWidth: 'auto', p: 0.5 }}
                                    >
                                        <ClearIcon fontSize="small" />
                                    </Button>
                                </InputAdornment>
                            ),
                        }}
                        sx={{ flex: 1, minWidth: { xs: '100%', md: 250 } }}
                    />

                    {isMobile && filters.length > 0 && (
                        <Button
                            variant="outlined"
                            size="small"
                            fullWidth
                            startIcon={<FilterListIcon />}
                            endIcon={filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            onClick={() => setFiltersExpanded(!filtersExpanded)}
                            sx={{ height: 40 }}
                        >
                            Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
                        </Button>
                    )}
                </Box>

                <Collapse in={filtersExpanded || !isMobile}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {filters.map((filter) => renderFilter(filter))}

                        {hasActiveFilters && (
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<ClearIcon />}
                                onClick={onClearFilters}
                                sx={{ height: 40, minWidth: { xs: '100%', sm: 'auto' } }}
                            >
                                Clear All
                            </Button>
                        )}
                    </Box>
                </Collapse>

                {activeFilterCount > 0 && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                            Active filters:
                        </Box>
                        {Object.entries(activeFilters).map(([field, value]) => {
                            if (!value || value === '') return null;

                            // Check for date range
                            if (typeof value === 'object' && 'from' in value && 'to' in value) {
                                if (!value.from && !value.to) return null;
                            }

                            const filter = filters.find((f) => f.field === field);
                            if (!filter) return null;

                            const displayValue = getFilterDisplayValue(filter, value);
                            if (!displayValue) return null;

                            return (
                                <Chip
                                    key={field}
                                    label={`${filter.label}: ${displayValue}`}
                                    size="small"
                                    onDelete={() => onFilterChange?.(field, filter.type === 'daterange' ? { from: null, to: null } : '')}
                                    color="primary"
                                    variant="outlined"
                                />
                            );
                        })}
                    </Box>
                )}
            </Stack>
        </Paper>
    );
};

